import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, colones, initials } from "@/lib/api";
import { calcularAguinaldoLive } from "@/lib/aguinaldo";
import { computePayrollForEmployee } from "@/lib/payroll";
import { buildPuestosMap, puestoNoHorasExtras } from "@/lib/puestos";
import { generarComprobanteAguinaldo } from "@/lib/aguinaldoPDF";
import { reporteAguinaldoExcel, reporteAguinaldoPDF } from "@/lib/aguinaldoReporte";
import { Coins, FileDown, ChevronLeft, ChevronRight, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Aguinaldo() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [empleados, setEmpleados] = useState([]);
  const [asistencia, setAsistencia] = useState([]);
  const [rebajos, setRebajos] = useState([]);
  const [bonificaciones, setBonificaciones] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Rango del período de aguinaldo: 01-dic (year−1) a 30-nov (year).
  const rango = useMemo(() => ({
    inicio: `${year - 1}-12-01`,
    fin: `${year}-11-30`,
  }), [year]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [e, emp, a, r, b, ovr] = await Promise.all([
          api.get("/empleados"),
          api.get("/empresa"),
          api.get(`/asistencia?fecha_inicio=${rango.inicio}&fecha_fin=${rango.fin}`),
          api.get("/rebajos"),
          api.get("/bonificaciones"),
          api.get("/planilla-overrides"),
        ]);
        setEmpleados(e.data || []);
        setEmpresa(emp.data || {});
        setAsistencia(a.data || []);
        setRebajos(r.data || []);
        setBonificaciones(b.data || []);
        setOverrides(ovr.data || []);
      } finally { setLoading(false); }
    })();
  }, [rango.inicio, rango.fin]);

  const puestosMap = useMemo(() => buildPuestosMap(empresa), [empresa]);

  const filas = useMemo(() => empleados
    .filter(e => e.estado === "Activo")
    .map(emp => ({
      emp,
      ...calcularAguinaldoLive({
        empleado: emp, allAsistencia: asistencia, allRebajos: rebajos, allBonif: bonificaciones, allOverrides: overrides,
        empresa, year, computePayrollForEmployee, puestosMap, puestoNoHorasExtras,
      }),
    })),
    [empleados, asistencia, rebajos, bonificaciones, overrides, empresa, year, puestosMap]);

  const totalGeneral = filas.reduce((s, f) => s + f.aguinaldo, 0);

  const descargar = async (f) => {
    const result = generarComprobanteAguinaldo({
      empresa, empleado: f.emp, year,
      desglose: f.desglose, aguinaldo: f.aguinaldo,
    });
    // Archivar como comprobante de tipo "aguinaldo"
    try {
      await api.post("/comprobantes", {
        empleado_id: f.emp.id,
        periodo_label: `Aguinaldo ${year}`,
        kind: "aguinaldo",
        fecha_inicio: `${year - 1}-12-01`,
        fecha_fin: `${year}-11-30`,
        monto_bruto: f.aguinaldo,
        monto_ccss: 0,
        monto_rebajos: 0,
        monto_neto: f.aguinaldo,
        file_name: result.fileName,
        pdf_base64: result.pdfBase64,
      });
    } catch (err) { console.error(err); }
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><Coins size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Aguinaldo</h1>
            <p className="text-sm text-secondary-c">Cálculo automático conforme al Código de Trabajo (período dic {year - 1} → nov {year}).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              try { reporteAguinaldoExcel({ empresa, year, filas }); toast.success("Reporte Excel generado"); }
              catch (e) { toast.error("Error: " + e.message); }
            }}
            disabled={filas.length === 0}
            className="np-btn np-btn-outline disabled:opacity-40"
            title="Descargar reporte de aguinaldo en Excel"
            data-testid="agui-report-excel-btn"
          >
            <FileSpreadsheet size={16} className="mr-1.5" /> Excel
          </button>
          <button
            onClick={() => {
              try { reporteAguinaldoPDF({ empresa, year, filas }); toast.success("Reporte PDF generado"); }
              catch (e) { toast.error("Error: " + e.message); }
            }}
            disabled={filas.length === 0}
            className="np-btn np-btn-outline disabled:opacity-40"
            title="Descargar reporte de aguinaldo en PDF"
            data-testid="agui-report-pdf-btn"
          >
            <FileText size={16} className="mr-1.5" /> PDF
          </button>
          <button onClick={() => setYear(y => y - 1)} className="np-btn np-btn-outline p-2" data-testid="agui-year-prev"><ChevronLeft size={16} /></button>
          <div className="px-4 py-2 bg-[#FAFAF7] rounded-xl text-sm font-medium font-display" data-testid="agui-year-label">Aguinaldo {year}</div>
          <button onClick={() => setYear(y => y + 1)} className="np-btn np-btn-outline p-2" data-testid="agui-year-next"><ChevronRight size={16} /></button>
        </div>
      </header>

      <div className="np-card p-4 mb-5 flex items-center justify-between flex-wrap gap-4">
        <div className="text-sm text-secondary-c">
          Base imponible: suma en vivo de los brutos mensuales (dic-{year - 1} a nov-{year}) calculados desde asistencia, rebajos y bonificaciones. Aguinaldo = total ÷ 12. Se actualiza automáticamente si modificás los datos.
        </div>
        <div className="text-sm">
          <span className="text-secondary-c">Total a pagar: </span>
          <span className="font-semibold text-primary-c" data-testid="agui-total">{colones(totalGeneral)}</span>
        </div>
      </div>

      {loading ? (
        <div className="np-card p-10 text-center text-secondary-c">Cargando...</div>
      ) : (
      <div className="np-card p-0 np-scroll">
          <table className="np-table text-sm">
            <thead><tr>
              <th>Empleado</th>
              <th>Cédula</th>
              <th className="text-right">Total Salarios (12 meses)</th>
              <th className="text-right">Aguinaldo</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-secondary-c">No hay empleados activos.</td></tr>
              )}
              {filas.map(f => {
                const open = expanded === f.emp.id;
                return (
                  <React.Fragment key={f.emp.id}>
                    <tr data-testid={`agui-row-${f.emp.cedula}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          {f.emp.avatar
                            ? <img src={f.emp.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                            : <div className="w-9 h-9 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-xs font-semibold">{initials(`${f.emp.nombre} ${f.emp.apellidos}`)}</div>}
                          <div>
                            <div className="font-medium">{f.emp.nombre} {f.emp.apellidos}</div>
                            <div className="text-xs text-secondary-c">{f.emp.puesto}</div>
                          </div>
                        </div>
                      </td>
                      <td>{f.emp.cedula}</td>
                      <td className="text-right">{colones(f.totalSalarios)}</td>
                      <td className="text-right font-semibold text-success">{colones(f.aguinaldo)}</td>
                      <td className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => setExpanded(open ? null : f.emp.id)} className="text-xs text-primary-c underline" data-testid={`agui-toggle-${f.emp.cedula}`}>{open ? "Ocultar" : "Ver desglose"}</button>
                          <button onClick={() => descargar(f)} disabled={f.aguinaldo === 0} className="np-btn np-btn-outline p-1.5 disabled:opacity-40" title="Generar comprobante de aguinaldo" data-testid={`agui-pdf-${f.emp.cedula}`}>
                            <FileDown size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-[#FAFAF7]">
                        <td colSpan={5} className="px-6 py-4">
                          <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-2">Desglose mensual</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {f.desglose.map((m) => (
                              <div key={`${m.anio}-${m.label}`} className="p-2 rounded-lg bg-white border border-c">
                                <div className="text-[10px] text-secondary-c uppercase tracking-widest">{m.anio}</div>
                                <div className="text-sm font-medium">{m.label}</div>
                                <div className={`text-xs ${m.salario > 0 ? "text-primary-c" : "text-secondary-c"}`}>{colones(m.salario)}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
